import { Badge } from '@design-system/badge';
import { formatConfirmationDate } from '../../helpers/confirmation-display';
import type {
  DetailCardPresentation,
  DetailField,
} from '../../types/presentation.types';
import { TiaThreadAvatar } from '../tia/tia-thread-avatar';

const EMPTY = '—';

function statusVariant(raw: string) {
  const v = raw.trim().toLowerCase();
  if (/(active|paid|completed|done|won|approved|success|confirmed)/.test(v))
    return 'success' as const;
  if (
    /(failed|cancelled|canceled|rejected|overdue|lost|inactive|unpaid)/.test(v)
  )
    return 'danger' as const;
  if (/(pending|draft|progress|partial|awaiting|review)/.test(v))
    return 'warning' as const;
  return 'default' as const;
}

function FieldValue({ field }: { field: DetailField }) {
  const { value, type } = field;
  if (value === null || value === undefined || value === '') {
    return <span className='text-gray-400'>{EMPTY}</span>;
  }
  const text = typeof value === 'string' ? value : String(value);
  if (type === 'badge') {
    return <Badge variant={statusVariant(text)}>{text}</Badge>;
  }
  if (type === 'date' && typeof value === 'string') {
    return <span>{formatConfirmationDate(value)}</span>;
  }
  return <span>{text}</span>;
}

/**
 * Figma success / detail row (node 20324:93525):
 * robot avatar + green title, then the same soft KV card as confirmation.
 */
export function AgentDetailCard({
  block,
  headline,
}: {
  block: DetailCardPresentation;
  /** Prefer the correlated assistant text (e.g. “Lead created successfully”). */
  headline?: string;
}) {
  const title = (headline?.trim() || block.title).trim();

  return (
    <div
      data-slot='agent-detail-card'
      className='flex w-full flex-col gap-1'
    >
      <div className='flex items-center gap-1'>
        <TiaThreadAvatar />
        <p className='text-base text-[#00b637]'>{title}</p>
      </div>

      {block.fields.length > 0 && (
        <dl className='flex w-full flex-col gap-2 rounded-2xl border border-primary-200 bg-primary-50 p-4'>
          {block.fields.map((f) => (
            <div
              key={f.key}
              className='flex items-center justify-between gap-3 text-sm font-medium leading-[23px] tracking-wide text-gray-800'
            >
              <dt className='shrink-0'>{f.label}</dt>
              <dd className='min-w-0 break-words text-right'>
                <FieldValue field={f} />
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
